import React, { Key, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Descriptions, Flex, Button, List, Typography, Empty, Space, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import SText from '@/styles/SText';
import { InventoryLocationItem, LocationLockEnum, OrderAddV2, WLocation } from '@/types/inventoryV2';
import { locationFetch } from '@/apis/inventoryv2';
import STableLayout, { TableLayoutRef } from '@/styles/STableLayout';
import { AnyObject } from 'antd/es/_util/type';
import { getLocationIdx } from '@/utils/wLocation';
import MoveLocationForm from '../../Inventory/components/MoveLocationForm';
import LocationOutbound from '../../Inventory/components/LocationOutbound';
import { LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { TableRowSelection } from 'antd/es/table/interface';
import BatchMoveLocation from './BatchMoveLocation';
import { numFormmater } from '@/pages/Requirement/util';
import { DictContext } from '@/layout/context/DictContext';
import ExportExcelButton from '@/components/ExportExcelButton';

interface LocationDetailProps {
  location: WLocation;
  onClose: () => void;
}

const LocationDetail: React.FC<LocationDetailProps> = ({
  location,
  onClose
}) => {
  const { t } = useTranslation();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const { locationTypes } = useContext(DictContext);
  const [loading, setLoading] = useState(false);
  const [locationData, setLocationData] = useState<WLocation | null>(null);
  const [barcodeInfo, setBarcodeInfo] = useState<InventoryLocationItem | null>(null);
  const [locItem, setLocItem] = useState<InventoryLocationItem | null>(null);
  const [outboundItem, setOutboundItem] = useState<InventoryLocationItem | null>(null);

  const [selectedRowKeys, setSelectedRowKeys] = useState<Array<Key>>([]);
  const [selectedRows, setSelectedRows] = useState<Array<InventoryLocationItem>>([]);

  const [open, setOpen] = useState<boolean>(false);

  const locationTypeInfo = locationTypes.find(info => info.key === location.type);

  useEffect(() => {
    if (location?.id) {
      getLocation(location.id);
    }
  }, [location?.id]);

  const getLocation = (locId: string) => {
    setLoading(true);
    locationFetch(locId)
      .then((data) => {
        setLocationData(data);
      })
      .catch((err) => {
        console.error('Failed to fetch location data:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const columns: ColumnsType<InventoryLocationItem> = [
    {
      title: t('warehouse.batchNo'),
      key: 'batchNo',
      width: 300,
      fixed: 'left',
      render: (_, record) => <SText text={record.lotItem?.lot?.lot || ''} />
    },
    {
      title: t('warehouse.supplierBatchNo'),
      key: 'supplierBatchNo',
      width: 150,
      render: (_, record) => <SText text={record.lotItem?.lot?.thdLot || ''} />
    },
    {
      title: t('purchase.productInfo'),
      children: [
        {
          title: <SText text={t('purchase.brand')} />,
          key: 'originalBrand',
          width: 120,
          render: (_, record) => <SText text={record.lotItem?.ext?.originalBrand || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('purchase.mpn')} />,
          key: 'originalCode',
          width: 120,
          render: (_, record) => <SText text={record.lotItem?.ext?.originalCode || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('purchase.dcc')} />,
          key: 'dcc',
          width: 110,
          render: (_, record) => <SText text={record.lotItem?.ext?.dcc != null ? String(record.lotItem.ext.dcc) : ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.code')} />,
          key: 'productCode',
          width: 120,
          render: (_, record) => <SText text={record.lotItem?.product?.code || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.name')} />,
          key: 'productName',
          width: 150,
          render: (_, record) => <SText text={record.lotItem?.product?.name || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.brand1')} />,
          key: 'model',
          width: 120,
          render: (_, record) => <SText text={record.lotItem?.product?.model || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.uom')} />,
          key: 'uom',
          width: 80,
          render: (_, record) => <SText text={record.lotItem?.product?.uom || ''} style={{ width: "100%" }} />
        },
      ]
    },
    {
      title: t('order.quantity'),
      dataIndex: 'count',
      key: 'count',
      width: 100,
      fixed: 'right',
      render: (value) => numFormmater(value || 0)
    },
    {
      title: t('warehouse.preIn'),
      dataIndex: 'preIn',
      key: 'preIn',
      width: 100,
      fixed: 'right',
      render: (value) => numFormmater(value || 0)
    },
    {
      title: t('warehouse.preOut'),
      dataIndex: 'preOut',
      key: 'preOut',
      width: 100,
      fixed: 'right',
      render: (value) => numFormmater(value || 0)
    },
    {
      title: t('common.opt'),
      fixed: 'right',
      width: 240,
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => setBarcodeInfo(record)}>
            {t('warehouse.viewBarcode')}
          </Button>
          {locationData?.lockState !== LocationLockEnum.LOCKED && (
            <>
              {
                record.count > record.preOut &&
                <Button onClick={() => setLocItem({ ...record, location: locationData as WLocation })}>
                  {t('warehouse.moveLoc')}
                </Button>
              }              <Button type="primary" onClick={() => setOutboundItem(record)}>
                {t('warehouse.outbound')}
              </Button>
            </>
          )}
        </Space>
      )
    },
  ];

  // 使用locationData的lots数据，它已经是InventoryLocationItem数组
  const dataSource = useMemo(() => {
    return locationData?.lots || [];
  }, [locationData])

  const batchExportColumns = [
    { key: 'batchNo', label: t('warehouse.batchNo') },
    { key: 'supplierBatchNo', label: t('warehouse.supplierBatchNo') },
    { key: 'originalBrand', label: t('purchase.brand') },
    { key: 'originalCode', label: t('purchase.mpn') },
    { key: 'dcc', label: t('purchase.dcc') },
    { key: 'productCode', label: t('product.code') },
    { key: 'productName', label: t('product.name') },
    { key: 'model', label: t('product.brand1') },
    { key: 'uom', label: t('product.uom') },
    { key: 'count', label: t('order.quantity') },
    { key: 'preIn', label: t('warehouse.preIn') },
    { key: 'preOut', label: t('warehouse.preOut') },
  ];

  const batchDefaultSelectedColumns = [
    'batchNo',
    'productCode',
    'productName',
    'model',
    'uom',
    'count',
    'preIn',
    'preOut'
  ];

  const batchExportDataMapper = (item: InventoryLocationItem) => ({
    [t('warehouse.batchNo')]: item.lotItem?.lot?.lot ?? '',
    [t('warehouse.supplierBatchNo')]: item.lotItem?.lot?.thdLot ?? '',
    [t('purchase.brand')]: item.lotItem?.ext?.originalBrand ?? '',
    [t('purchase.mpn')]: item.lotItem?.ext?.originalCode ?? '',
    [t('purchase.dcc')]: item.lotItem?.ext?.dcc ?? '',
    [t('product.code')]: item.lotItem?.product?.code ?? '',
    [t('product.name')]: item.lotItem?.product?.name ?? '',
    [t('product.brand1')]: item.lotItem?.product?.model ?? '',
    [t('product.uom')]: item.lotItem?.product?.uom ?? '',
    [t('order.quantity')]: item.count ?? 0,
    [t('warehouse.preIn')]: item.preIn ?? 0,
    [t('warehouse.preOut')]: item.preOut ?? 0,
  });

  const outSucc = (data: OrderAddV2) => {
    // 重新请求数据
    getLocation(location.id);
    setOutboundItem(null);
  }

  const rowSelection: TableRowSelection<InventoryLocationItem> = {
    selectedRowKeys,
    onChange(selectedRowKeys, selectedRows) {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRows(selectedRows)
    },
  }

  const handleBatchMove = () => {
    setOpen(true);
    console.log(selectedRows);

  };

  return (
    <Modal
      title={
        <Space>
          <SText text={location.number} />
          <Tag >{locationTypeInfo?.desc}</Tag>
        </Space>
      }
      open={!!location}
      onCancel={onClose}
      footer={null}
      width={1200}
      destroyOnClose
    >
      <Flex vertical gap={16} style={{ height: '70vh', overflow: 'hidden' }}>
        {locationData ? (
          <Descriptions bordered column={2}>
            <Descriptions.Item label={t('warehouse.locationIndex')}>
              {getLocationIdx(locationData)}
            </Descriptions.Item>
            <Descriptions.Item label={t('warehouse.lotCount')}>
              {dataSource.length}
            </Descriptions.Item>
            <Descriptions.Item label={t('counting.lockState')}>
              <Tag
                color={locationData?.lockState === LocationLockEnum.LOCKED ? 'red' : 'green'}
                icon={locationData?.lockState === LocationLockEnum.LOCKED ? <LockOutlined /> : <UnlockOutlined />}
              >
                {locationData?.lockState === LocationLockEnum.LOCKED ? t('counting.counting') : t('counting.notCounting')}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <div style={{ height: '128px' }} />
        )}
        <STableLayout
          rowSelection={rowSelection as unknown as TableRowSelection<AnyObject>}
          rowKey={"id"}
          headerChildren={
            <Flex gap={10}>
              <Button onClick={handleBatchMove} disabled={selectedRows.length === 0}>{t('批量移库')}</Button>
              <ExportExcelButton<InventoryLocationItem>
                list={dataSource}
                columns={batchExportColumns}
                defaultSelectedColumns={batchDefaultSelectedColumns}
                dataMapper={batchExportDataMapper}
                fileName={`${location.number}.xlsx`}
                sheetName={t('warehouse.batchNo')}
                btnText={t('warehouse.exportExcel')}
                disabled={loading || dataSource.length === 0}
              />
            </Flex>
          }
          tableLayoutRef={tableRef}
          total={dataSource.length}
          dataSource={dataSource}
          handleChange={() => { }}
          columns={columns as ColumnsType<AnyObject>}
          loading={loading}
          scrollX={1600}
          pagination={{ pageSize: 10 }}
        />
      </Flex>

      <Modal
        title={t('warehouse.viewBarcode')}
        open={!!barcodeInfo}
        onCancel={() => setBarcodeInfo(null)}
        footer={null}
        width={600}
        destroyOnClose
      >
        {barcodeInfo && (
          <Flex vertical gap={16}>
            <Descriptions bordered column={1}>
              <Descriptions.Item label={t('warehouse.batchNo')}>
                {barcodeInfo.lotItem?.lot?.lot || ''}
              </Descriptions.Item>
            </Descriptions>

            <Typography.Title level={5}>{t('warehouse.barcodeList')}</Typography.Title>

            {barcodeInfo.barcodes && barcodeInfo.barcodes.length > 0 ? (
              <List
                bordered
                dataSource={barcodeInfo.barcodes as unknown as string[]}
                renderItem={(barcode: string) => (
                  <List.Item>
                    <Typography.Text copyable>{barcode}</Typography.Text>
                  </List.Item>
                )}
                style={{ maxHeight: '400px', overflow: 'auto' }}
              />
            ) : (
              <Empty />
            )}
          </Flex>
        )}
      </Modal>
      {locItem && <MoveLocationForm locItem={locItem} onClose={() => setLocItem(null)} onOk={() => { setLocItem(null); getLocation(location.id) }} />}
      {outboundItem && (
        <LocationOutbound
          lotItems={[outboundItem]}
          open={!!outboundItem}
          onCancel={() => setOutboundItem(null)}
          locationNo={locationData?.number}
          onSuccess={outSucc}
        />
      )}
      {
        open && <BatchMoveLocation
          open={open}
          selectedRows={selectedRows}
          onClose={() => setOpen(false)}
          onMoveSuccess={() => {
            setOpen(false);
            getLocation(location.id);
          }}
          locationId={locationData?.id ?? ''}
        />
      }
    </Modal>
  );
};

export default LocationDetail;
